import { PropertyChange } from '../BaseCommandHandler';
import { Component } from '@/types';

type PatternHandler = (match: RegExpMatchArray, component: Component) => PropertyChange | null;
type PatternMap = Map<RegExp, PatternHandler>;

/**
 * Factory: range/min/max/step/value patterns for numeric controls
 */
export function createRangePatterns(): PatternMap {
  const patterns: PatternMap = new Map();

  patterns.set(
    /(?:set )?range (\d+(?:\.\d+)?) (?:to|-) (\d+(?:\.\d+)?)/i,
    (match) => ({ properties: { min: parseFloat(match[1]), max: parseFloat(match[2]) } })
  );

  patterns.set(
    /(?:set )?min(?:imum)? (?:to |= ?)?(\d+(?:\.\d+)?)/i,
    (match) => ({ properties: { min: parseFloat(match[1]) } })
  );

  patterns.set(
    /(?:set )?max(?:imum)? (?:to |= ?)?(\d+(?:\.\d+)?)/i,
    (match) => ({ properties: { max: parseFloat(match[1]) } })
  );

  patterns.set(
    /(?:set )?step (?:to |= ?)?(\d+(?:\.\d+)?)/i,
    (match) => ({ properties: { step: parseFloat(match[1]) } })
  );

  patterns.set(
    /(?:set )?value (?:to |= ?)?(\d+(?:\.\d+)?)/i,
    (match) => ({ properties: { value: parseFloat(match[1]) } })
  );

  return patterns;
}

/**
 * Factory: color property patterns for named color props
 */
export function createColorPatterns(colorPropNames: string[]): PatternMap {
  const patterns: PatternMap = new Map();

  // Sort by name length descending so longer names (e.g. "inactiveColor") are
  // checked before shorter substrings (e.g. "activeColor")
  const sorted = [...colorPropNames].sort((a, b) => b.length - a.length);

  for (const propName of sorted) {
    // Convert camelCase to space-separated words for regex: "onColor" -> "on color"
    const readable = propName.replace(/([A-Z])/g, ' $1').toLowerCase().trim();
    const regex = new RegExp(`(?:set )?\\b${readable} (?:to )?([\\w#]+(?:\\([^)]*\\))?)`, 'i');
    patterns.set(regex, (match) => {
      const color = match[1]?.trim();
      if (!color) return null;
      return { properties: { [propName]: color } };
    });
  }

  // Generic "color <value>" pattern (applies to first prop)
  if (colorPropNames.length > 0) {
    patterns.set(
      /(?:set )?color (?:to )?([#\w]+(?:\([^)]*\))?)/i,
      (match) => {
        const color = match[1]?.trim();
        if (!color) return null;
        return { properties: { [colorPropNames[0]]: color } };
      }
    );
  }

  return patterns;
}

/**
 * Factory: theme selection patterns with aliases
 */
export function createThemePatterns(
  validThemes: string[],
  aliases: Record<string, string> = {}
): PatternMap {
  const patterns: PatternMap = new Map();

  const resolveTheme = (input: string): string | null => {
    const normalized = input.toLowerCase().replace(/[\s-]+/g, '-');
    if (validThemes.includes(normalized)) return normalized;
    if (aliases[normalized]) return aliases[normalized];
    // Fuzzy: find theme containing the input
    const match = validThemes.find(t => t.includes(normalized) || normalized.includes(t));
    return match ?? null;
  };

  patterns.set(
    /(?:set |change |use )?theme (?:to )?(.+)/i,
    (match) => {
      const theme = resolveTheme(match[1].trim());
      return theme ? { properties: { theme } } : null;
    }
  );

  // "make it <theme>" pattern
  const themeAlts = [...validThemes, ...Object.keys(aliases)].join('|').replace(/-/g, '[- ]?');
  if (themeAlts) {
    patterns.set(
      new RegExp(`(?:make (?:it )?|use )(${themeAlts})`, 'i'),
      (match) => {
        const theme = resolveTheme(match[1].trim());
        return theme ? { properties: { theme } } : null;
      }
    );
  }

  return patterns;
}

/**
 * Factory: on/off/toggle patterns for boolean properties
 */
export function createTogglePatterns(propertyName: string): PatternMap {
  const patterns: PatternMap = new Map();

  patterns.set(
    /\b(?:turn |switch )?on\b(?!\s+(?:color|label))/i,
    () => ({ properties: { [propertyName]: true } })
  );

  patterns.set(
    /\b(?:turn |switch )?off\b(?!\s+(?:color|label))/i,
    () => ({ properties: { [propertyName]: false } })
  );

  patterns.set(
    /\btoggle\b/i,
    (_match, component) => {
      const current = component.properties?.[propertyName];
      return { properties: { [propertyName]: !current } };
    }
  );

  patterns.set(
    /\b(?:enable|activate)\b/i,
    () => ({ properties: { [propertyName]: true } })
  );

  patterns.set(
    /\b(?:disable|deactivate)\b/i,
    () => ({ properties: { [propertyName]: false } })
  );

  return patterns;
}

/**
 * Factory: unit setting patterns (%, px, etc.)
 */
export function createUnitPatterns(): PatternMap {
  const patterns: PatternMap = new Map();

  patterns.set(
    /(?:set )?unit (?:to )?(.+)/i,
    (match) => {
      const unit = match[1].trim();
      return { properties: { unit } };
    }
  );

  patterns.set(
    /(?:show |display )?(?:in |as )?(percent(?:age)?|degrees?|celsius|fahrenheit|%|px|rpm|psi|bar)/i,
    (match) => {
      const unitMap: Record<string, string> = {
        'percent': '%', 'percentage': '%', '%': '%',
        'degree': '°', 'degrees': '°',
        'celsius': '°C', 'fahrenheit': '°F',
        'px': 'px', 'rpm': 'RPM', 'psi': 'PSI', 'bar': 'bar',
      };
      const unit = unitMap[match[1].toLowerCase()] || match[1];
      return { properties: { unit } };
    }
  );

  return patterns;
}
